import tokens from '@kluein/design-tokens';
import { Box, Button, Layer, ThemeContext } from '@kluein/klue-ui';
import { Sparkle, X } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCheckOwnOrganization } from 'pages/knowledge-hub/hooks/useCheckOwnOrganization';
import { useKnowledgeHubSteps } from 'pages/knowledge-hub/hooks/useKnowledgeHubSteps';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import { KnowledgeHubChecklist } from '../knowledge-hub-checklist/KnowledgeHubChecklist';

export function KnowledgeHubWidget() {
  // TODO: Think about a better approach to create own organization
  useCheckOwnOrganization();

  const { progress, currentStep, steps, isError, isLoading, isDone } =
    useKnowledgeHubSteps();
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const { t } = useTranslation(['KnowledgeHub']);
  const isStarted = progress > 0;

  if (isError || !steps || steps?.length === 0 || !currentStep || isDone) {
    return null;
  }

  return (
    <>
      <ThemeContext.Extend
        value={{
          layer: {
            container: {
              extend: {
                zIndex: 100,
                transition: 'background 0.4s ease-in-out',
                cursor: 'default',
                top: 0,
                left: 0,
                position: 'absolute !important',
                width: '100% !important',
                height: `${isWidgetOpen ? '100%' : 0} !important`,
                maxHeight: '100% !important',
                maxWidth: '100% !important',
                minHeight: '0 !important',
                background: isWidgetOpen
                  ? 'rgba(1, 22, 39, 0.4)'
                  : 'transparent',
              },
            },
          },
        }}
      >
        <Layer
          data-test-id="test-id-knowledge-hub-widget"
          position="top-right"
          margin={{
            top: '65px',
            right: '20px',
          }}
          background="transparent"
          animation="fadeIn"
          modal={false}
        >
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Button
                variant="highlight-action"
                leftSlot={isWidgetOpen ? <X /> : <Sparkle />}
                size="large"
                style={{
                  borderRadius: '48px',
                  position: 'fixed',
                  right: '20px',
                  top: '65px',
                  zIndex: 10,
                  boxShadow: tokens.boxShadow.small,
                }}
                onClick={() => setIsWidgetOpen((prev) => !prev)}
                dropContent={
                  <Box
                    fill
                    background="linear-gradient(180deg, #FFFFFF 84.68%, #FCFCFF 92.95%, #F1F0FF 100%)"
                  >
                    <KnowledgeHubChecklist
                      progress={progress}
                      currentStep={currentStep}
                      steps={steps}
                      isLoading={isLoading}
                      isWidget
                    />
                  </Box>
                }
                dropContentVisible={isWidgetOpen}
                dropProps={{
                  onClickOutside: (e) => {
                    if (
                      (e.target as HTMLElement).closest(
                        `[data-test-id="${TEST_IDS.knowledgeHub.widget.button}"]`,
                      )
                    ) {
                      return;
                    }

                    setIsWidgetOpen(false);
                  },
                  align: {
                    top: 'bottom',
                    right: 'right',
                  },
                  margin: { top: 'medium' },
                  round: 'xsmall',
                  width: '400px',
                  border: {
                    color: tokens.color.primitives.violet[150],
                    size: '1px',
                  },
                }}
                data-test-id={TEST_IDS.knowledgeHub.widget.button}
                data-tracking-id={TRACKING_IDS.knowledgeHub.widget.button}
              >
                {!isWidgetOpen
                  ? t(
                      `KnowledgeHub:widget.${
                        isStarted ? 'started' : 'notStarted'
                      }.callToAction`,
                    )
                  : null}
              </Button>
            </motion.div>
          </AnimatePresence>
        </Layer>
      </ThemeContext.Extend>
    </>
  );
}
